import { useRef, useEffect, useCallback } from "react"
import { debounce } from "lodash"

interface UseScrollPositionOptions {
  storageKey: string
  isOpen: boolean
}

/**
 * Safe localStorage operations that handle errors gracefully
 */
const safeLocalStorage = {
  getItem: (key: string): string | null => {
    try {
      return localStorage.getItem(key)
    } catch {
      return null
    }
  },
  setItem: (key: string, value: string): boolean => {
    try {
      localStorage.setItem(key, value)
      return true
    } catch {
      return false
    }
  },
}

/**
 * Custom hook to manage scroll position persistence in localStorage
 * @param storageKey - The localStorage key to store the scroll position
 * @param isOpen - Whether the scrollable container is currently open/visible
 * @returns Object containing the ref to attach to the scrollable element
 */
export function useScrollPosition({ storageKey, isOpen }: UseScrollPositionOptions) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const prevIsOpen = useRef(isOpen)

  // Save scroll position to localStorage
  const saveScrollPosition = useCallback(() => {
    if (scrollRef.current) {
      const scrollTop = scrollRef.current.scrollTop
      safeLocalStorage.setItem(storageKey, String(scrollTop))
    }
  }, [storageKey])

  // Restore scroll position from localStorage when opened
  useEffect(() => {
    if (scrollRef.current && isOpen) {
      const savedPosition = safeLocalStorage.getItem(storageKey)
      if (savedPosition !== null) {
        const position = Number(savedPosition)
        // Use requestAnimationFrame to ensure DOM is ready
        requestAnimationFrame(() => {
          if (scrollRef.current) {
            scrollRef.current.scrollTop = position
          }
        })
      }
    }
  }, [isOpen, storageKey])

  // Save scroll position when panel closes
  useEffect(() => {
    // Check if transitioning from open to closed
    if (prevIsOpen.current === true && isOpen === false) {
      saveScrollPosition()
    }
    prevIsOpen.current = isOpen
  }, [isOpen, saveScrollPosition])

  // Attach scroll event listener with debouncing
  useEffect(() => {
    const scrollElement = scrollRef.current
    if (!scrollElement) return

    // Create debounced handler
    const debouncedSave = debounce(saveScrollPosition, 150)

    scrollElement.addEventListener("scroll", debouncedSave)
    return () => {
      scrollElement.removeEventListener("scroll", debouncedSave)
    }
  }, [saveScrollPosition])

  return {
    scrollRef,
  }
}
